import { useSyncExternalStore } from 'react'

/**
 * MeetUbot cài lên màn hình chính như một ứng dụng (PWA): service worker `public/sw.js`, manifest
 * `public/manifest.webmanifest`. Chrome / Edge / Android báo "có thể cài" bằng sự kiện
 * `beforeinstallprompt` (giữ lại để nút "Cài ứng dụng" gọi); iPhone / iPad không có sự kiện này,
 * phải cài bằng Chia sẻ → Thêm vào MH chính.
 */

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: InstallPromptEvent | null = null
const listeners = new Set<() => void>()
const changed = () => listeners.forEach((listener) => listener())

export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true

export const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

/** Gọi một lần lúc khởi động (trước khi React vẽ: sự kiện cài đặt đến sớm). */
export function setupPwa(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred = event as InstallPromptEvent
    changed()
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    changed()
  })
  // Dev (Vite) không dùng service worker: tránh bản cũ lưu lại làm rối lúc sửa mã.
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => undefined))
  }
}

/** `prompt`: hiện hộp cài của trình duyệt; null khi trình duyệt chưa cho cài (hoặc đã cài). */
export function useInstallPrompt(): (() => Promise<boolean>) | null {
  const available = useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => deferred !== null,
  )
  if (!available) return null
  return async () => {
    const event = deferred
    if (!event) return false
    await event.prompt()
    const { outcome } = await event.userChoice
    deferred = null
    changed()
    return outcome === 'accepted'
  }
}

/** Đăng ký service worker (null khi chưa có: dev, trình duyệt không hỗ trợ). */
export async function swRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  return (await navigator.serviceWorker.getRegistration()) ?? null
}
